import { FC, useState, MouseEvent, useEffect } from 'react';
import { GridRowId } from '@mui/x-data-grid';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import {
  actionButtons,
  apiUrls,
  bankDialogTitles,
  bankDialogSubtitles,
  bankStatusOptions,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { Bank } from 'src/models/Management.interface';
import { BankStatus } from 'src/models/enums/BankStatus.enum';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { bankInitialValues } from 'src/utils/initialValues.utils';
import { bankValidationSchema } from 'src/utils/validationScheme.utils';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import TextField from 'src/components/InputFields/TextField';
import AccountTypeField from 'src/components/InputFields/BankAccountType';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { calculateDialogHeight } from 'src/utils/dialog.utils';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import FormikSelectField, {
  Option,
} from 'src/components/InputFields/FormikSelectField';
import {
  FlexContainerBox,
  FlexRowStartBox,
  FlexColumnBox,
} from '../DialogBox/POBox/POBox.styles';
import { fetchBankNames } from 'src/services/enums.service';

const BankActionMenuButton: FC<{
  rowId: GridRowId;
  accountNumber: string;
  status: BankStatus;
  fetchBanks: () => void;
  deleteBank: (id: string) => void;
}> = ({ rowId, accountNumber, status, fetchBanks, deleteBank }) => {
  const { showSnackbar } = StyledSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Bank | null>(null);
  const [newStatus, setNewStatus] = useState<BankStatus>(status);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [bankOptions, setBankOptions] = useState<Option[]>([]);
  const [bankList, setBankList] = useState<Bank | null>(null);
  const open = Boolean(anchorEl);

  const handleStatusChange = (updatedStatus: BankStatus) => {
    setNewStatus(updatedStatus);
  };

  const fetchBankList = async () => {
    try {
      const response = await fetchBankNames();
      const options = response.map((value: string) => ({
        label: value,
        value: value,
      }));
      setBankOptions(options);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditFormSubmit = async () => {
    if (!selectedAccount) {
      return;
    }
    try {
      const response = await axiosTokenInstance.put(apiUrls.EDIT_BANK, {
        id: String(rowId),
        accountNumber: formik.values.accountNumber,
        bankName: formik.values.bankName,
        branchName: formik.values.branchName,
        accountName: formik.values.accountName,
        accountType: formik.values.accountType,
      });
      setEditDialogOpen(false);
      fetchBanks();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogOpen = async (bank: Bank) => {
    setSelectedAccount(bank);
    fetchBankList();

    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_BANK_INFO_BY_ID,
        {
          params: { id: bank.id },
        }
      );

      formik.setValues({
        bankName: response.data.bankName,
        branchName: response.data.branchName,
        accountName: response.data.accountName,
        accountNumber: accountNumber,
        accountType: response.data.accountType,
        status: response.data.status,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedAccount(null);
    formik.resetForm();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async () => {
    try {
      if (dialogType === actionButtons.DELETE) {
        if (rowId) {
          deleteBank(String(rowId));
        }
        fetchBanks();
      } else if (dialogType === actionButtons.UPDATE) {
        const response = await axiosTokenInstance.patch(
          apiUrls.BANK_ACTIVATION,
          {
            id: rowId,
            status: newStatus,
          }
        );
        showSnackbar(response.data.message, 'success');
        fetchBanks();
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
      setSelectedAccount(null);
    }
  };

  const formik = useFormik({
    initialValues: bankInitialValues,
    validationSchema: bankValidationSchema,
    onSubmit: handleEditFormSubmit,
  });

  const getDialogHeight = () =>
    calculateDialogHeight(formik, [
      ['bankName', 'branchName'],
      ['accountName', 'accountNumber'],
      ['accountType'],
    ]);

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={() =>
              handleEditDialogOpen({
                id: String(rowId),
                bankName: '',
                branchName: '',
                accountName: '',
                accountNumber,
                accountType: '',
                status: BankStatus.ACTIVE,
              })
            }
            iconClassName="bx bx-edit"
            text="Edit"
          />

          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={editDialogOpen}
            handleClose={handleEditDialogClose}
            title={bankDialogTitles.EDIT}
            subtitle={bankDialogSubtitles.EDIT}
            onSubmit={formik.handleSubmit}
            smScreen={false}
            buttonLabel="Update"
          >
            {selectedAccount && (
              <FlexContainerBox>
                <FlexRowStartBox>
                  <FlexColumnBox>
                    <FormikSelectField
                      id="bankName"
                      label="Bank Name"
                      formikProps={formik}
                      options={bankOptions}
                      width="294px"
                    />
                  </FlexColumnBox>

                  <FlexColumnBox>
                    <TextField
                      id="branchName"
                      label="Branch"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>
                </FlexRowStartBox>

                <FlexRowStartBox>
                  <FlexColumnBox>
                    <TextField
                      id="accountName"
                      label="Account Holder's Name"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>

                  <FlexColumnBox>
                    <TextField
                      id="accountNumber"
                      label="Account Number"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>
                </FlexRowStartBox>

                <FlexRowStartBox>
                  <FlexColumnBox>
                    <AccountTypeField
                      id="accountType"
                      label="Account Type"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>
                </FlexRowStartBox>
              </FlexContainerBox>
            )}
          </CustomDialog>

          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.UPDATE)}
            iconClassName="bx bx-refresh"
            text="Update Status"
          />

          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ConfirmationDialog
        height={dialogType === actionButtons.DELETE ? '275px' : '352px'}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={
          dialogType === actionButtons.DELETE
            ? bankDialogTitles.DELETE
            : bankDialogTitles.UPDATE_STATUS
        }
        subtitle={
          dialogType === actionButtons.DELETE
            ? bankDialogSubtitles.DELETE
            : bankDialogSubtitles.UPDATE_STATUS
        }
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : 'var(--warning)'
        }
        alertIconColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : 'var(--warning)'
        }
        status={status}
        dialogType={
          dialogType === actionButtons.UPDATE ? dialogType : undefined
        }
        onStatusChange={handleStatusChange}
        statusOptions={bankStatusOptions.map((option) => option.value)}
      />
    </>
  );
};
export default BankActionMenuButton;
